/* ---------------------------------------------------------------------------
   Demand forces on a simple span: what the loads ask of the member.

   Reproduces the `DFDLLL` sheet — "Demand Forces, Dead Load and Live Load" —
   which every family shares in the same shape.

   UNITS, ONCE, HERE. This program mixes them freely and the workbooks label
   almost nothing, so every field below says what it is in. Spans and widths in
   METRES, section dimensions in MILLIMETRES, stresses in MPa, forces in kN,
   moments in kN-m, vehicle weights in TONNES. Where a value is per metre of
   width — which is how a slab is rated — the field name says `PerMetre`.

   SIMPLE SPANS ONLY. The continuous workbooks do not compute their demands:
   they take DL, SDL and LL at midspan and at the pier from a separate
   structural analysis, typed in. That is the phase-1 decision recorded in the
   handover, and it is a limit of the 2009 program rather than of this code — an
   influence-line beam solver would remove the manual step later. Nothing in this
   file pretends to cover it; `continuousDemand` takes the typed forces and says
   where they came from.

   EMPTY IS NOT ZERO. Every quantity a caller passes is a required number, and
   there are no defaults. Excel treats a blank cell as zero, so an unfilled
   asphalt thickness silently becomes a bridge with no wearing course and a
   rating that is too generous. Here the application must supply a value and say
   where it came from, or not compute at all.
   --------------------------------------------------------------------------- */

import { round2 } from './round.ts';
import { impactFor, liveMoment, liveShear, type VehicleId } from './vehicles.ts';
import type { ExteriorDfRule } from './profile.ts';

/* --------------------------- dead load --------------------------- */

/**
 * What sits on the span before any traffic does.
 *
 * All of it per metre of the width the member carries — which for a flat slab is
 * a one-metre strip and for a girder is the girder's own share.
 */
export interface DeadLoadInput {
  /** Structural depth carrying the load, m. Slab thickness for RCFS. */
  thickness: number;
  /** Width the load is spread over, m. `bw` on the LAYOUT sheet. */
  width: number;
  /** Carriageway width the wearing course covers, m. */
  carriageway: number;
  /** Wearing-course thickness, MILLIMETRES — the one field the sheet keeps in mm. */
  wearingCourse: number;
  /** Weight of one barrier rail, kN/m. */
  barrierRail: number;
  /** Concrete unit weight, kN/m3. 24 throughout the reference pack. */
  concreteUnitWeight: number;
  /** Asphalt unit weight, kN/m3. 22 throughout. */
  asphaltUnitWeight: number;
}

export interface DeadLoad {
  /** Self weight of the structural section, kN/m per metre of width. */
  selfWeight: number;
  /** Barrier rails, kN/m per metre of width. See the note on BARRIER_SPREAD. */
  barrier: number;
  /** Asphalt overlay, kN/m per metre of width. */
  overlay: number;
  /**
   * The three added and rounded to two decimals.
   *
   * THE ROUNDING IS CARRIED FORWARD, as `DFDLLL!J15` does, and it reaches the
   * rating factor: 14.4507 becomes 14.45 and the moment becomes 115.6 rather
   * than 115.606. Correction C10.
   */
  total: number;
  /** The same sum unrounded, so the size of that rounding is visible. */
  totalExact: number;
  /** Moment at midspan, kN-m per metre of width. wL^2/8. */
  moment: number;
  /** Shear at the support, kN per metre of width. wL/2. */
  shear: number;
}

/**
 * The factor the workbook applies to the barrier rail.
 *
 * `DFDLLL!J13` is `Wbr/bw*1.25`: one rail's weight spread over the full width,
 * times 1.25. Neither the sheet nor the study notes record where 1.25 comes
 * from. It is not two rails (that would be 2.0) and it is not a load factor
 * (dead load is factored later, at 1.3). Reproduced faithfully because it is on
 * the reference path and changing it would move every RC rating in the pack;
 * recorded as correction C14 so the report says so, and flagged for confirmation
 * against the manual.
 */
export const BARRIER_SPREAD = 1.25;

/** Reproduces `DFDLLL!J12:J17`. `span` in metres. */
export function deadLoad(input: DeadLoadInput, span: number): DeadLoad {
  /* bw*h*Wu/bw. The width cancels — the sheet writes it out anyway, and so does
     this, because a reader checking against the cell should see the same
     expression rather than a simplified one. */
  const selfWeight = (input.width * input.thickness * input.concreteUnitWeight) / input.width;
  const barrier = (input.barrierRail / input.width) * BARRIER_SPREAD;
  const overlay =
    (input.wearingCourse * input.carriageway * input.asphaltUnitWeight) / input.width / 1000;

  const totalExact = selfWeight + barrier + overlay;
  const total = round2(totalExact);

  return {
    selfWeight,
    barrier,
    overlay,
    total,
    totalExact,
    moment: (span * span * total) / 8,
    shear: (span * total) / 2,
  };
}

/* --------------------------- distribution --------------------------- */

/**
 * How many wheel lines the member being rated actually carries.
 *
 * Three rules in the program, and which one applies is a property of the family
 * rather than of the bridge, so the caller names it.
 */
export type DistributionKind = 'slab' | 'interior-girder' | 'exterior-girder';

export interface Distribution {
  /** Wheel lines per metre of width (slab) or per girder. */
  wheelLines: number;
  /** The rule, as the manual writes it, for the report. */
  formula: string;
  /** True where the slab rule hit its 7-foot cap. */
  capped: boolean;
}

/** Feet per metre, the conversion the sheet writes out as 0.3048 each time. */
const FT = 0.3048;

/**
 * Wheel lines per metre of a flat slab, `DFDLLL!E22`.
 *
 * AASHTO's slab distribution width E = 4 + 0.06L in FEET, capped at 7 ft — the
 * one place this program leaves metric — inverted to wheel lines per metre.
 */
export function slabDistribution(span: number): Distribution {
  const e = 4 + (0.06 * span) / FT;
  if (e > 7) {
    return { wheelLines: 1 / FT / 7, formula: '1 / (0.3048 x 7), E capped at 7 ft', capped: true };
  }
  return { wheelLines: 1 / FT / e, formula: '(1/0.3048) / (4 + 0.06L/0.3048)', capped: false };
}

/**
 * Wheel lines per girder, `S/1.829` and its exterior counterpart.
 *
 * 1.829 m is 6 ft, the standard wheel-line spacing. The exterior rule is the
 * open ruling — see `ExteriorDfRule` and correction C11 — so it is passed in
 * rather than chosen here.
 */
export function girderDistribution(
  spacing: number,
  kind: 'interior-girder' | 'exterior-girder',
  rule: ExteriorDfRule,
): Distribution {
  if (kind === 'interior-girder') {
    return { wheelLines: spacing / 1.829, formula: 'S / 1.829', capped: false };
  }
  const fraction = (spacing - 1.829) / spacing;
  if (rule === 'manual') {
    return { wheelLines: fraction, formula: '(S - 1.829) / S  [MLRB text]', capped: false };
  }
  return { wheelLines: 1 + fraction, formula: '1 + (S - 1.829) / S  [workbook]', capped: false };
}

/* --------------------------- live load --------------------------- */

export interface LiveLoadDemand {
  vehicle: VehicleId;
  /** Wheel lines the member carries, and where that came from. */
  distribution: Distribution;
  /** The impact fraction applied. */
  impact: number;
  /** Moment per wheel line without impact, kN-m. The rounded lookup. */
  momentPerWheelLine: number;
  /** Shear per wheel line without impact, kN. */
  shearPerWheelLine: number;
  /** Moment on the member, with distribution and impact. kN-m. */
  moment: number;
  /** Shear on the member, with distribution and impact. kN. */
  shear: number;
  /** The Appendix III lines used, for the report. */
  formulas: { moment: string; shear: string };
}

/**
 * The live-load demand one vehicle puts on one member.
 *
 * Reproduces `DFDLLL!J25:J28` for the rating vehicle and `POSTLL!J12:J27` for
 * each posting vehicle — the same arithmetic, which is why there is one function
 * rather than two sheets.
 */
export function liveLoadDemand(
  vehicle: VehicleId,
  span: number,
  distribution: Distribution,
): LiveLoadDemand {
  const m = liveMoment(vehicle, span);
  const v = liveShear(vehicle, span);
  const impact = impactFor(span);
  const factor = distribution.wheelLines * (1 + impact);

  return {
    vehicle,
    distribution,
    impact,
    momentPerWheelLine: m.value,
    shearPerWheelLine: v.value,
    moment: m.value * factor,
    shear: v.value * factor,
    formulas: { moment: m.formula, shear: v.formula },
  };
}

/* --------------------------- continuous spans --------------------------- */

/**
 * Demands for a continuous span, which this program does not compute.
 *
 * The continuous workbooks take these from a separate structural analysis and
 * the user types them in. `analysis` is not decoration: a rating whose demands
 * came from an unnamed source is not reproducible, and the report prints this
 * string. See the header.
 */
export interface ContinuousDemand {
  /** Dead load moment, kN-m. Negative at a pier, by the usual convention. */
  moment: number;
  /** Superimposed dead load moment, kN-m. */
  superimposedMoment: number;
  /** Live load moment including impact, kN-m. */
  liveMoment: number;
  shear: number;
  superimposedShear: number;
  liveShear: number;
  /** What produced these — the model, the software, the run, the date. */
  analysis: string;
}
